import { api } from '../api/client';
import type { RoomDocument } from '../api/types';

/**
 * The room's PROCESSING STATUS page (owner ask 2026-10-12), shared by the admin
 * and the seller apps: GET /rooms/:id/processing — every uploaded document with
 * its filing result and its background OCR / redaction state, one selection and
 * an inclusive upload-day range (the room's timezone) at a time, paginated.
 */
export const PROCESSING_FILTERS = [
  'all',
  'classifying',
  'needs_filing',
  'ocr_processing',
  'redaction_processing',
  'needs_review',
  'failed',
  'completed',
] as const;
export type ProcessingFilter = (typeof PROCESSING_FILTERS)[number];

export const PROCESSING_FILTER_LABEL: Record<ProcessingFilter, string> = {
  all: 'All',
  classifying: 'Classifying',
  needs_filing: 'Needs filing',
  ocr_processing: 'OCR processing',
  redaction_processing: 'Redaction processing',
  needs_review: 'Needs review',
  failed: "Couldn't file",
  completed: 'Completed',
};

export const PROCESSING_FILTER_HINT: Record<ProcessingFilter, string> = {
  all: 'Every uploaded document in the date range.',
  classifying: 'The AI is still reading and filing these.',
  needs_filing: 'The AI was not sure of the category — a person chooses it.',
  ocr_processing: 'Scanned pages being read (OCR) in the background.',
  redaction_processing: 'Personal-data redaction running in the background.',
  needs_review: 'A person must act: uncertain redactions, or OCR / redaction failed.',
  failed: 'The AI could not file these at all — file them by hand.',
  completed: 'Filed, with OCR and redaction done or not needed.',
};

export interface ProcessingViewResponse {
  documents: RoomDocument[];
  counts: Record<ProcessingFilter, number>;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** The room's timezone the upload days are read in. */
  timeZone: string;
}

/** Upload-date presets; `custom` = "Between [from] and [to]". */
export const UPLOAD_RANGES = [
  'all',
  'today',
  'last7',
  'last30',
  'this_month',
  'last_month',
  'custom',
] as const;
export type UploadRange = (typeof UPLOAD_RANGES)[number];

export const UPLOAD_RANGE_LABEL: Record<UploadRange, string> = {
  all: 'All time',
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Between…',
};

/** The page's state — everything lives in the URL. */
export interface ProcessingParams {
  status: ProcessingFilter;
  range: UploadRange;
  /** Only for `custom`: inclusive `YYYY-MM-DD` bounds (either may be open). */
  from?: string;
  to?: string;
  page: number;
  order: 'desc' | 'asc';
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Read the page's state from the URL (unknown values fall back to the defaults). */
export function processingParamsFrom(search: URLSearchParams): ProcessingParams {
  const status = search.get('status');
  const range = search.get('range');
  const from = search.get('from');
  const to = search.get('to');
  const page = Number(search.get('page'));
  return {
    status: (PROCESSING_FILTERS as readonly string[]).includes(status ?? '')
      ? (status as ProcessingFilter)
      : 'all',
    range: (UPLOAD_RANGES as readonly string[]).includes(range ?? '')
      ? (range as UploadRange)
      : 'all',
    ...(from && ISO_DAY.test(from) ? { from } : {}),
    ...(to && ISO_DAY.test(to) ? { to } : {}),
    page: Number.isInteger(page) && page > 1 ? page : 1,
    order: search.get('order') === 'asc' ? 'asc' : 'desc',
  };
}

/** The page's state as URL search params (defaults omitted). */
export function processingSearch(p: ProcessingParams): URLSearchParams {
  const s = new URLSearchParams();
  if (p.status !== 'all') s.set('status', p.status);
  if (p.range !== 'all') s.set('range', p.range);
  if (p.range === 'custom') {
    if (p.from) s.set('from', p.from);
    if (p.to) s.set('to', p.to);
  }
  if (p.page > 1) s.set('page', String(p.page));
  if (p.order === 'asc') s.set('order', 'asc');
  return s;
}

/** Today's `YYYY-MM-DD` in `timeZone` (UTC on an unknown zone). */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The inclusive `from`/`to` days a range stands for, in the room's timezone. */
export function uploadRangeDays(
  p: Pick<ProcessingParams, 'range' | 'from' | 'to'>,
  timeZone: string,
  now: Date = new Date(),
): { from?: string; to?: string } {
  const today = todayIn(timeZone, now);
  const firstOfMonth = `${today.slice(0, 8)}01`;
  switch (p.range) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'this_month':
      return { from: firstOfMonth, to: today };
    case 'last_month': {
      const lastDayPrev = addDays(firstOfMonth, -1);
      return { from: `${lastDayPrev.slice(0, 8)}01`, to: lastDayPrev };
    }
    case 'custom':
      return { from: p.from, to: p.to };
    default:
      return {};
  }
}

export const PROCESSING_PAGE_SIZE = 50;

export function processingQueryKey(roomId: string, p: ProcessingParams, days: { from?: string; to?: string }) {
  return ['rooms', roomId, 'processing', p.status, days.from ?? '', days.to ?? '', p.page, p.order];
}

export function fetchProcessingView(
  roomId: string,
  p: ProcessingParams,
  days: { from?: string; to?: string },
): Promise<ProcessingViewResponse> {
  const q = new URLSearchParams({
    status: p.status,
    page: String(p.page),
    pageSize: String(PROCESSING_PAGE_SIZE),
    order: p.order,
  });
  if (days.from) q.set('from', days.from);
  if (days.to) q.set('to', days.to);
  return api.get<ProcessingViewResponse>(`/rooms/${roomId}/processing?${q.toString()}`);
}

/** Documents still moving (classifying, OCR or redaction running) in the range. */
export function processingInFlight(counts: ProcessingViewResponse['counts']): number {
  return counts.classifying + counts.ocr_processing + counts.redaction_processing;
}

/**
 * What the shared poll cadence (`nextDocumentsPoll`) reads for this page: the
 * page's rows, plus — while anything in the range is in flight, on this page or
 * not — one marker whose id carries the in-flight counts, so a change in them
 * counts as progress and keeps the fast cadence.
 */
export function processingPollDocs(
  view: ProcessingViewResponse | undefined,
): Pick<RoomDocument, 'id' | 'classificationStatus' | 'processingStatus'>[] {
  if (!view) return [];
  const rows = view.documents.map((d) => ({
    id: d.id,
    classificationStatus: d.classificationStatus,
    processingStatus: d.processingStatus,
  }));
  if (processingInFlight(view.counts) === 0) return rows;
  const c = view.counts;
  return [
    ...rows,
    {
      id: `counts:${c.classifying}/${c.ocr_processing}/${c.redaction_processing}`,
      classificationStatus: 'processing',
      processingStatus: 'processing',
    },
  ];
}
